<script>
    import Transition from "../components/Transition.svelte";
    import {onMount} from "svelte";
    import {locomotivesUrl, locomotiveSoundsUrl} from "../urls";
    import {customFetch} from "../customFetch";
    import {notifications} from "../notifications";
    import StraightTable from "../components/StraightTable.svelte";
    import {
        binaryView,
        dateView,
        dropdownView,
        editableImageView,
        editableBigIconView,
        valueView,
        saveRows, labelView, buttonView
    } from "../tableUtils";

    onMount(init);

    let originalRows = {};
    let rows = [];

    async function load() {
        try {
            let response = await customFetch(locomotivesUrl());
            let data = await response.json();
            for (let i = 0; i < data.length; i++) {
                data[i].UpdatedAt = new Date(data[i].UpdatedAt)
                data[i].Changes = new Set();
                originalRows[data[i].ID] = JSON.parse(JSON.stringify(data[i]));
            }
            rows = data;
        } catch (e) {
            notifications.danger(
                `Error loading locomotives: ${e.message}`
            );
        }
    }

    function init() {
        load();
    }

    async function save(e) {
        const rows = e.detail.data;
        let fields = columns.map(elem => elem.field);
        await saveRows(rows, fields, locomotivesUrl());
        await load();
    }

    let columns = [
        {title: 'ID', field: 'ID', sortable: true, defaultValue: null, editView: labelView},
        {title: 'Icon', field: 'NewIcon', sortable: false, defaultValue: null, editView: editableImageView()},
        {title: 'BigIcon', field: 'NewBigIcon', sortable: false, defaultValue: null, editView: editableBigIconView()},
        {title: 'Model', field: 'NewModel', sortable: false, defaultValue: null, editView: binaryView("fbx", "ModelData")},
        {title: 'Speed', field: 'Speed', sortable: true, defaultValue: 0, editView: valueView("number", 0.01, 0, 10)},
        {title: 'Acceleration', field: 'Acceleration', sortable: true, defaultValue: 0, editView: valueView("number", 0.01, 0, 10)},
        {title: 'Deceleration', field: 'Deceleration', sortable: true, defaultValue: 0, editView: valueView("number", 0.01, 0, 10)},
        {title: 'Railcars Count', field: 'RailcarsCount', sortable: true, defaultValue: 0, editView: valueView("number", 0.01, 0, 10)},
        {title: 'Buy Price', field: 'BuyPrice', sortable: true, defaultValue: 0, editView: valueView("number", 1000, 0)},
        {title: 'Length', field: 'Length', sortable: true, defaultValue: 0, editView: valueView("number", 0.01, 0, 10)},
        {title: 'Updated At', field: 'UpdatedAt', sortable: true, defaultValue: new Date(), editView: dateView},
        {title: 'Epoch', field: 'Epoch', sortable: true, defaultValue: 1, editView: valueView("number", 1, 0)},
        {title: 'Column', field: 'Column', sortable: true, defaultValue: 1, editView: valueView("number", 1, 0)},
        {title: 'Order', field: 'Order', sortable: true, defaultValue: 0, editView: valueView("number", 1, 0)},
        {title: 'Default', field: 'Default', sortable: true, defaultValue: false, editView: valueView("checkbox")},
        {title: 'Sounds', field: 'Sounds', editView: buttonView("Sounds", (row) => selectedValue = row)},
    ];

    let selectedValue = null;
    let sounds = []
    async function loadSounds(selectedValue) {
        if (selectedValue) {
            let data = await customFetch(locomotiveSoundsUrl(selectedValue.ID));
            sounds = await data.json();
        } else {
            sounds = [];
        }
    }
    $: {
        selectedValue && loadSounds(selectedValue);
    }

    function handleClose() {
        selectedValue = null;
    }

    async function saveSounds(e) {
        const rows = e.detail.data;
        let fields = soundColumns.map(elem => elem.field);
        await saveRows(rows, fields, locomotiveSoundsUrl(selectedValue.ID));
        await loadSounds(selectedValue)
    }

    let soundTypes = { DistantFull: 'DistantFull', DistantEmpty: 'DistantEmpty', CloseFull: 'CloseFull', CloseEmpty: 'CloseEmpty' };

    let soundColumns = [
        {title: 'Sound', field: 'NewSound', sortable: false, defaultValue: null, editView: binaryView("ogg", "SoundData")},
        {title: 'SoundType', field: 'SoundType', sortable: true, defaultValue: soundTypes[0], editView: dropdownView(soundTypes)},
    ];

    function guessType(value) {
        value = value.toLowerCase()
        let isFull = value.indexOf("full") >= 0;
        let isDistant = value.indexOf("distant") >= 0;
        let isClose = value.indexOf("close") >= 0;
        let isEmpty = value.indexOf("empty") >= 0;
        if (isDistant && isFull) {
            return soundTypes.DistantFull;
        } else if (isDistant && isEmpty) {
            return soundTypes.DistantEmpty;
        } else if (isClose && isFull) {
            return soundTypes.CloseFull;
        } else if (isClose && isEmpty) {
            return soundTypes.CloseEmpty;
        } else {
            return soundTypes.DistantFull; // default?
        }
    }

    function handleDrop(event) {
        event.preventDefault();
        let files = [...event.dataTransfer.files];
        let newData = Object.assign([], sounds);
        for (let i = 0; i < files.length; i++) {
            newData.push({value: files[i], NewSound: files[i], SoundType: guessType(files[i].name)})
        }
        sounds = newData;
    }

    function handleDragOver(event) {
        event.preventDefault();
    }
</script>

{#if selectedValue}
    <div class="inline fixed top-0 right-0 left-0 bottom-0 bg-black opacity-25 z-10"></div>
    <div tabindex="-1" aria-hidden="true" class="fixed top-0 left-0 right-0 z-50 w-full p-4 overflow-auto md:inset-0 h-[calc(100%-1rem)] max-h-full" style="min-width: max-content;">
        <div class="relative w-full max-h-full">
            <div class="relative bg-white rounded-lg shadow">
                <div class="p-6 space-y-6">
                    <div class="grid">
                        <div>
                            <span>{selectedValue.ID}</span>
                            <button type="button" class="text-gray-400 bg-transparent hover:bg-gray-200 hover:text-gray-900 rounded-lg text-sm p-1.5 ml-auto inline-flex items-center" on:click={handleClose}>
                                <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd"></path></svg>
                            </button>
                        </div>
                        <div class="object-contain">
                            <StraightTable columns={soundColumns} data={sounds} on:change={saveSounds} settings={{canResetRows:false}}></StraightTable>
                            <div
                                class="dropzone"
                                on:drop={handleDrop}
                                on:dragover={handleDragOver}
                                on:dragenter={() => (event.target.classList.add('dragover'))}
                                on:dragleave={() => (event.target.classList.remove('dragover'))}
                            >
                                Drop your files here
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
{/if}

<Transition>
    <StraightTable data={rows} {columns} on:change={save}/>
</Transition>

<style>
    .dropzone {
        width: 100%;
        height: 100px;
        border: 2px dashed #ccc;
        border-radius: 5px;
        display: flex;
        align-items: center;
        justify-content: center;
        text-align: center;
        color: #ccc;
        font-size: 16px;
    }
    .dropzone.dragover {
        border-color: #000;
        color: #000;
    }
</style>